import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { sowConversationService } from '../../services/sowConversationService';
import { sowService } from '../../services/sowService';
import { SOW } from '../../models/sow';
import { ChecklistSection } from '../../components/SOW/ChecklistSection';
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  Upload,
  AlertCircle,
  Camera,
  Sparkles,
  Plus
} from 'lucide-react';
import { useAuthToken } from '../../components/AuthTokenProvider';

interface ChecklistItem {
  id: string;
  label: string;
  type: 'checkbox' | 'yes_no' | 'select' | 'text';
  options?: string[];
  placeholder?: string;
}

interface ChecklistSectionData {
  id: string;
  title: string;
  type: 'global' | 'job';
  jobType?: string;
  items: ChecklistItem[];
}

interface JobImage {
  imageId: string;
  jobId: string;
  description: string;
  url?: string;
}

export const SOWChecklistPage: React.FC = () => {
  const { sowId } = useParams<{ sowId: string }>();
  const navigate = useNavigate();
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();

  const isPublicMode = window.location.pathname.startsWith('/scope/');

  const [sow, setSOW] = useState<SOW | null>(null);
  const [sections, setSections] = useState<ChecklistSectionData[]>([]);
  const [answers, setAnswers] = useState<Record<string, Record<string, any>>>({});
  const [jobImages, setJobImages] = useState<JobImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasRecommendations, setHasRecommendations] = useState(false);
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Per-job photo upload state
  const [uploadingForJob, setUploadingForJob] = useState<string | null>(null);

  useEffect(() => {
    if (sowId && (isPublicMode || (tokenSet && !tokenLoading))) {
      loadData();
    }
  }, [sowId, tokenSet, tokenLoading, isPublicMode]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const loadData = async () => {
    if (!sowId) return;

    try {
      setLoading(true);
      setError(null);

      const sowData = await sowService.getSOW(sowId, !isPublicMode);
      setSOW(sowData);

      // Load checklists
      const checklistResponse = await sowConversationService.getQuestionnaire(sowId, !isPublicMode);

      const checklistSections: ChecklistSectionData[] = checklistResponse.sections.map((section: any) => ({
        id: section.id,
        title: section.title,
        type: section.type || 'job',
        jobType: section.jobType,
        items: section.questions.map((q: any) => ({
          id: q.id,
          label: q.text,
          type: q.type === 'single_choice' ? 'select' : q.type,
          options: q.options,
          placeholder: q.placeholder
        }))
      }));

      setSections(checklistSections);

      // Load images that have been assigned to jobs
      if (sowData.images && sowData.images.length > 0) {
        const assignedImages: JobImage[] = sowData.images
          .filter((img: any) => img.jobId && img.status === 'analyzed')
          .map((img: any) => ({
            imageId: img.imageId || img.id,
            jobId: img.jobId || img.component,
            description: img.description || '',
            url: img.url || ''
          }));
        setJobImages(assignedImages);
      }

      // Initialize answers
      const initialAnswers: Record<string, Record<string, any>> = {};
      checklistSections.forEach(section => {
        initialAnswers[section.id] = {};
      });

      // Restore saved answers
      if (sowData.questionnaireAnswers && sowData.questionnaireAnswers.length > 0) {
        sowData.questionnaireAnswers.forEach((savedAnswer: any) => {
          const sectionId = savedAnswer.section;
          const questionId = savedAnswer.questionId;
          const answer = savedAnswer.answer;
          if (sectionId && questionId && initialAnswers[sectionId]) {
            initialAnswers[sectionId][questionId] = answer;
          }
        });
      }

      // Apply AI-recommended answers (from photos-first generate-scope)
      // Only apply if no saved answers exist (don't overwrite user changes)
      const recommendedAnswers = (sowData as any).recommendedAnswers;
      if (recommendedAnswers && (!sowData.questionnaireAnswers || sowData.questionnaireAnswers.length === 0)) {
        setHasRecommendations(true);
        Object.entries(recommendedAnswers).forEach(([jobId, rec]: [string, any]) => {
          const checkedItems = rec.checkedItems || [];
          if (initialAnswers[jobId]) {
            checkedItems.forEach((itemId: string) => {
              initialAnswers[jobId][itemId] = true;
            });
          }
        });
      }

      setAnswers(initialAnswers);

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load project data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save answers with debouncing
  const saveAnswers = async (answersToSave: Record<string, Record<string, any>>) => {
    if (!sowId) return;

    try {
      setSaving(true);

      const formattedAnswers: Array<{ questionId: string; answer: string | boolean; section?: string }> = [];
      Object.entries(answersToSave).forEach(([sectionId, sectionAnswers]) => {
        Object.entries(sectionAnswers).forEach(([questionId, answer]) => {
          if (answer !== undefined && answer !== null && answer !== '') {
            formattedAnswers.push({ questionId, answer, section: sectionId });
          }
        });
      });

      if (formattedAnswers.length > 0) {
        await sowConversationService.saveQuestionnaireProgress(sowId, formattedAnswers, !isPublicMode);
      }
    } catch (err) {
      console.error('Error saving progress:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAnswerChange = (sectionId: string, itemId: string, value: any) => {
    const updatedAnswers = {
      ...answers,
      [sectionId]: {
        ...answers[sectionId],
        [itemId]: value
      }
    };
    setAnswers(updatedAnswers);

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveAnswers(updatedAnswers);
    }, 1000);
  };

  // Per-job photo addition
  const handleAddPhotoToJob = async (jobId: string, file: File) => {
    if (!sowId) return;

    try {
      setUploadingForJob(jobId);

      // Upload
      const imageId = await sowService.uploadImageForAnalysis(sowId, file, !isPublicMode);

      // Analyze and assign to job
      const result = await sowService.addImageToJob(sowId, imageId, jobId, !isPublicMode);

      // Add to local state
      setJobImages(prev => [...prev, {
        imageId,
        jobId,
        description: result.description || '',
        url: URL.createObjectURL(file)
      }]);
    } catch (err) {
      console.error('Error adding photo to job:', err);
    } finally {
      setUploadingForJob(null);
    }
  };

  const handleSubmit = async () => {
    if (!sowId) return;

    try {
      setSubmitting(true);
      setError(null);

      const formattedAnswers: any[] = [];
      sections.forEach(section => {
        const sectionAnswers = answers[section.id] || {};
        Object.entries(sectionAnswers).forEach(([itemId, value]) => {
          if (value === undefined || value === null || value === '') return;
          if (typeof value === 'boolean' && !value) return;
          formattedAnswers.push({
            questionId: itemId,
            answer: typeof value === 'boolean' ? 'Yes' : value,
            section: section.id
          });
        });
      });

      await sowConversationService.submitQuestionnaire(sowId, formattedAnswers, !isPublicMode);

      const overviewPath = isPublicMode ? `/scope/${sowId}` : `/sow/${sowId}`;
      navigate(overviewPath);

    } catch (err) {
      console.error('Error submitting checklist:', err);
      setError('Failed to submit checklist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    const overviewPath = isPublicMode ? `/scope/${sowId}` : `/sow/${sowId}`;
    navigate(overviewPath);
  };

  const getImagesForJob = (jobId: string) => {
    return jobImages.filter(img => img.jobId === jobId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading checklists...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-4" />
          <p className="text-gray-800 mb-4">{error}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const globalSection = sections.find(s => s.type === 'global');
  const jobSections = sections.filter(s => s.type === 'job');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold">Project Scope & Photos</h1>
            {saving && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </div>
            )}
          </div>
          <p className="text-gray-600">
            Review what work needs to be done. We'll use this to create your estimate.
          </p>
        </div>

        {/* AI Recommendation Banner */}
        {hasRecommendations && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    We pre-filled this based on your photos
                  </p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    Review and adjust the selections below, then hit "Get Estimate"
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Global project checklist */}
        {globalSection && (
          <ChecklistSection
            title={globalSection.title}
            items={globalSection.items}
            answers={answers[globalSection.id] || {}}
            onAnswerChange={(itemId, value) => handleAnswerChange(globalSection.id, itemId, value)}
          />
        )}

        {/* Job-specific sections */}
        {jobSections.map(section => {
          const sectionImages = getImagesForJob(section.id);
          const isUploading = uploadingForJob === section.id;

          return (
            <div key={section.id} className="space-y-4">
              {/* Section Title */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg px-6 py-4">
                <h2 className="text-xl font-bold">{section.title}</h2>
                <p className="text-blue-100 text-sm mt-1">
                  {sectionImages.length > 0
                    ? `${sectionImages.length} photo${sectionImages.length !== 1 ? 's' : ''} matched`
                    : 'Checklist for this area'
                  }
                </p>
              </div>

              {/* Photos for this job */}
              {(sectionImages.length > 0 || true) && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-sm flex items-center gap-2">
                        <Camera className="w-4 h-4 text-blue-600" />
                        Photos
                        {sectionImages.length > 0 && (
                          <Badge variant="outline" className="text-xs">{sectionImages.length}</Badge>
                        )}
                      </h3>
                    </div>

                    {sectionImages.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-3">
                        {sectionImages.map(img => (
                          <div key={img.imageId} className="space-y-1">
                            <div className="aspect-square rounded-lg overflow-hidden border border-green-200 bg-gray-100">
                              {img.url ? (
                                <img src={img.url} alt={img.description} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <Camera className="w-6 h-6" />
                                </div>
                              )}
                            </div>
                            {img.description && (
                              <div className="text-xs text-gray-500 line-clamp-1">{img.description}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Photo button */}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleAddPhotoToJob(section.id, file);
                          e.target.value = '';
                        }}
                        className="hidden"
                        id={`add-photo-${section.id}`}
                        disabled={isUploading}
                      />
                      <label
                        htmlFor={`add-photo-${section.id}`}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-dashed cursor-pointer transition-colors ${
                          isUploading
                            ? 'border-blue-300 bg-blue-50 text-blue-600'
                            : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-600 hover:text-blue-600'
                        }`}
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Plus className="w-3.5 h-3.5" />
                            Add Photo
                          </>
                        )}
                      </label>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Checklist */}
              <ChecklistSection
                title="What work needs to be done?"
                items={section.items}
                answers={answers[section.id] || {}}
                onAnswerChange={(itemId, value) => handleAnswerChange(section.id, itemId, value)}
              />
            </div>
          );
        })}

        {/* Submit button */}
        <div className="flex flex-col items-center gap-3">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-12"
            size="lg"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating Estimate...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Get Estimate
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
